import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { useUser } from '@fs/zion-user'
import { useLazyRequest } from '../hooks'
import useErrorHandlerOverlay from '../hooks/useErrorHandlerOverlay'
import { deleteTimeSlot, getUserSchedule, updateSchedule, updateTimeSlot } from '../api'
import useScheduleData from '../components/schedule/hooks/useScheduleData'
import { consolidateMySchedule } from '../components/schedule/helpers/scheduleHelpers'

interface TimeSlot {
  preferred: string
  alternates: string[]
}

type ScheduleDay = Record<string, TimeSlot>

interface MySchedule {
  days: Record<string, ScheduleDay>
  onDemand?: unknown[]
}

interface ScheduleValue {
  calendarDate: number
  contentId: string
  date: string
  time: string
}

interface RescheduledSession {
  previousDate: number
  date: number
  item: {
    contentId: string
    title?: string
    [key: string]: unknown
  }
}

interface MyScheduleContextValue {
  mySchedule: MySchedule | null
  rescheduledSessions: RescheduledSession[]
  loading: boolean
  isReady: boolean
  addToMySchedule: (value: ScheduleValue) => Promise<void>
  removeFromMySchedule: (value: ScheduleValue) => Promise<void>
}

/**
 * MyScheduleProvider
 *
 * App-level provider that manages the user's personal schedule.
 * Handles CRUD operations for adding/removing sessions to My Schedule.
 * Available globally so users can add sessions from search, schedule, or any page.
 *
 * Responsibilities:
 * - Fetch user's saved schedule from API
 * - Add/remove sessions with optimistic updates
 * - Consolidate saved schedule with current calendar data
 * - Handle rescheduled sessions
 * - Provide mySchedule state to all components
 */

const MyScheduleContext = createContext<MyScheduleContextValue | undefined>(undefined)

export const useMyScheduleContext = (): MyScheduleContextValue => {
  const context = useContext(MyScheduleContext)
  if (!context) {
    throw new Error('useMyScheduleContext must be used within MyScheduleProvider')
  }
  return context
}

/**
 * Adds a session to the user's schedule state (immutable)
 */
const addToMyScheduleState = (mySchedule: MySchedule, date: string, time: string, contentId: string): MySchedule => {
  const scheduleDay: ScheduleDay = mySchedule.days[date] || {}
  const timeslot = scheduleDay[time]

  if (timeslot) {
    // We already have a preferred session, add it to the alternates (immutably)
    const newTimeslot: TimeSlot = {
      ...timeslot,
      alternates: [...timeslot.alternates, contentId],
    }

    return {
      ...mySchedule,
      days: {
        ...mySchedule.days,
        [date]: {
          ...scheduleDay,
          [time]: newTimeslot,
        },
      },
    }
  }
  // We don't have the timeslot, add it to the date
  const timeSlot: ScheduleDay = {
    [time]: {
      preferred: contentId,
      alternates: [],
    },
  }

  return {
    ...mySchedule,
    days: { ...mySchedule.days, [date]: { ...scheduleDay, ...timeSlot } },
  }
}

/**
 * Removes a session from the user's schedule state (immutable)
 */
const removeFromMyScheduleState = (
  mySchedule: MySchedule,
  date: string,
  time: string,
  contentId: string
): ScheduleDay => {
  const scheduleDay: ScheduleDay = mySchedule.days[date] || {}
  const timeslot = scheduleDay[time]

  if (!timeslot) {
    return scheduleDay
  }

  const { preferred, alternates } = timeslot

  // If the preferred ID is removed and there are no alternates, delete the timeslot.
  if (contentId === preferred && alternates.length === 0) {
    return Object.fromEntries(Object.entries(scheduleDay).filter(([key]) => key !== time))
  }

  // Look for the contentId in the alternates array (immutably)
  if (alternates.includes(contentId)) {
    const newAlternates = alternates.filter((id) => id !== contentId)
    return {
      ...scheduleDay,
      [time]: {
        ...timeslot,
        alternates: newAlternates,
      },
    }
  }

  // If the preferred is the item to remove, promote first alternate (immutably)
  if (contentId === preferred && alternates.length > 0) {
    const [newPreferred, ...newAlternates] = alternates
    return {
      ...scheduleDay,
      [time]: {
        preferred: newPreferred,
        alternates: newAlternates,
      },
    }
  }

  // No changes needed
  return scheduleDay
}

interface MyScheduleProviderProps {
  children: ReactNode
}

export default function MyScheduleProvider({ children }: MyScheduleProviderProps): JSX.Element {
  const user = useUser()
  const [mySchedule, setMySchedule] = useState<MySchedule | null>(null)
  const [rescheduledSessions, setRescheduledSessions] = useState<RescheduledSession[]>([])

  // Track current user identity to detect sign-out/sign-in transitions
  const currentUserIdRef = useRef<string | null>(null)
  const hasFetchedRef = useRef(false)
  const consolidatedRef = useRef<MySchedule | null>(null)
  const lastCalendarDataRef = useRef<string | null>(null)

  // Fetch full schedule data for consolidation (only when user is signed in)
  const { loading: scheduleLoading, data: scheduleData } = useScheduleData({ skip: !user?.signedIn })

  // Transform schedule data to the format expected by consolidation
  const transformedScheduleData = useMemo(() => {
    return scheduleData.map(({ date, item }) => ({ item, date }))
  }, [scheduleData])

  // API hooks - keeping updateMyScheduleTimeslot and deleteMyScheduleTimeslot with useLazyRequest
  const [updateMyScheduleTimeslot] = useLazyRequest(updateTimeSlot)
  const [deleteMyScheduleTimeslot] = useLazyRequest(deleteTimeSlot)

  // Manual state management for user schedule (to prevent race conditions on user change)
  const [myScheduleApiState, setMyScheduleApiState] = useState<{
    data: MySchedule | null
    loading: boolean
    error: Error | null
  }>({ data: null, loading: false, error: null })

  // Error overlay for explicit fetch failures (not consolidation)
  useErrorHandlerOverlay(myScheduleApiState.error)

  // Custom fetch function with explicit state control
  const getMySchedule = useCallback(async (): Promise<void> => {
    const requestedUserId = currentUserIdRef.current
    setMyScheduleApiState({ data: null, loading: true, error: null })
    try {
      const response = await getUserSchedule()
      if (currentUserIdRef.current !== requestedUserId) return
      setMyScheduleApiState({ data: response.data, loading: false, error: null })
    } catch (error) {
      if (currentUserIdRef.current !== requestedUserId) return
      setMyScheduleApiState({ data: null, loading: false, error: error as Error })
    }
  }, [])

  /**
   * Creates a stable signature from calendar data to detect when sessions are rescheduled.
   * Returns a string that changes only when session IDs or dates change.
   */
  const createCalendarSignature = useCallback((data: { item: { contentId: string }; date: number }[]): string => {
    if (!data.length) return ''
    return JSON.stringify(
      data
        .map((d) => ({ id: d.item.contentId, date: d.date }))
        .sort((a, b) => a.id.localeCompare(b.id) || a.date - b.date)
    )
  }, [])

  /**
   * Silent wrapper for updateSchedule that bypasses global error overlay.
   * This is used for consolidation sync, which should fail silently.
   */
  const updateMyScheduleSilent = useCallback(async (schedulePayload: MySchedule): Promise<void> => {
    try {
      await updateSchedule(schedulePayload as unknown as Record<string, unknown>)
    } catch (error) {
      // Silent failure - no global error overlay triggered
      console.error('Failed to sync consolidated schedule:', error)
    }
  }, [])

  /**
   * Adds a session to the user's schedule
   */
  const addToMySchedule = useCallback(
    (value: ScheduleValue): Promise<void> => {
      // Guard against null mySchedule
      if (!mySchedule?.days) {
        return Promise.reject(new Error('Schedule not loaded'))
      }

      // Check 4-session limit per timeslot
      if ((mySchedule.days[value.date]?.[value.time]?.alternates?.length ?? 0) >= 3) {
        return Promise.reject(new Error('Maximum 4 sessions per timeslot'))
      }

      // Store original state for rollback
      const originalMySchedule = mySchedule

      // Optimistic update
      const updatedMySchedule = addToMyScheduleState(mySchedule, value.date, value.time, value.contentId)
      setMySchedule(updatedMySchedule)

      // Persist to API with rollback on error
      const body = updatedMySchedule.days[value.date][value.time]
      return updateMyScheduleTimeslot({ date: value.date, time: value.time }, body).catch((error) => {
        // Rollback on failure
        setMySchedule(originalMySchedule)
        throw error
      })
    },
    [mySchedule, updateMyScheduleTimeslot]
  )

  /**
   * Removes a session from the user's schedule
   */
  const removeFromMySchedule = useCallback(
    (value: ScheduleValue): Promise<void> => {
      // Guard against null mySchedule
      if (!mySchedule?.days) {
        return Promise.reject(new Error('Schedule not loaded'))
      }

      // Store original state for rollback
      const originalMySchedule = mySchedule

      // Optimistic update
      const updatedScheduleDay = removeFromMyScheduleState(mySchedule, value.date, value.time, value.contentId)
      const updatedMySchedule: MySchedule = {
        ...mySchedule,
        days: { ...mySchedule.days, [value.date]: updatedScheduleDay },
      }
      setMySchedule(updatedMySchedule)

      // Persist to API with rollback on error
      const body = updatedMySchedule.days[value.date][value.time]
      if (body !== undefined) {
        return updateMyScheduleTimeslot({ date: value.date, time: value.time }, body).catch((error) => {
          setMySchedule(originalMySchedule)
          throw error
        })
      }
      return deleteMyScheduleTimeslot({ date: value.date, time: value.time }).catch((error) => {
        setMySchedule(originalMySchedule)
        throw error
      })
    },
    [mySchedule, updateMyScheduleTimeslot, deleteMyScheduleTimeslot]
  )

  // Reset ALL schedule state on user identity change
  useEffect(() => {
    const userId = user?.signedIn ? (user.cisId ?? user.id ?? null) : null
    const previousUserId = currentUserIdRef.current

    // Detect identity change: sign-out, sign-in, or account switch
    if (previousUserId !== userId) {
      // Clear ALL schedule-related state atomically to prevent data leakage
      setMySchedule(null)
      setRescheduledSessions([])
      setMyScheduleApiState({ data: null, loading: false, error: null })

      // Reset tracking refs
      hasFetchedRef.current = false
      consolidatedRef.current = null
      lastCalendarDataRef.current = null

      // Update identity ref AFTER clearing state
      currentUserIdRef.current = userId
    }
  }, [user?.signedIn, user?.cisId, user?.id])

  // Fetch user's schedule when user signs in AND schedule is not loaded
  useEffect(() => {
    if (
      user?.signedIn &&
      !user.userLoading &&
      mySchedule === null &&
      !myScheduleApiState.loading &&
      !hasFetchedRef.current
    ) {
      hasFetchedRef.current = true
      getMySchedule()
    }
  }, [user?.signedIn, user?.userLoading, mySchedule, myScheduleApiState.loading, getMySchedule])

  // Set mySchedule from API data once loaded (before consolidation)
  useEffect(() => {
    if (!myScheduleApiState.loading && !myScheduleApiState.error && myScheduleApiState.data) {
      setMySchedule(myScheduleApiState.data)
    }
  }, [myScheduleApiState.data, myScheduleApiState.loading, myScheduleApiState.error])

  // Consolidate mySchedule with calendar data using silent wrapper
  useEffect(() => {
    let cancelled = false

    const consolidate = async (): Promise<void> => {
      // Create signature of current calendar data
      const calendarSignature = createCalendarSignature(transformedScheduleData)
      const calendarDataChanged = lastCalendarDataRef.current !== calendarSignature

      if (
        user?.signedIn &&
        mySchedule &&
        mySchedule !== consolidatedRef.current && // Prevent infinite loop: consolidation creates new object which triggers re-render
        calendarDataChanged && // Only when calendar data actually changes (not user add/remove operations)
        transformedScheduleData.length
      ) {
        // Consolidate the stored data from mySchedule with the calendar data.
        const { consolidatedMySchedule, rescheduledItems } = consolidateMySchedule(
          mySchedule,
          transformedScheduleData
        ) as { consolidatedMySchedule: MySchedule; rescheduledItems: RescheduledSession[] }

        if (cancelled) return

        // Call the service to save the consolidated mySchedule if there are session changes
        // Using silent wrapper to prevent global error overlay
        if (rescheduledItems.length) {
          await updateMyScheduleSilent(consolidatedMySchedule)
        }

        if (cancelled) return // Check again after async operation

        setMySchedule(consolidatedMySchedule)
        setRescheduledSessions(rescheduledItems)
        consolidatedRef.current = consolidatedMySchedule // Track consolidated state
        lastCalendarDataRef.current = calendarSignature // Track calendar data state
      }
    }

    consolidate()

    return () => {
      cancelled = true
    }
  }, [transformedScheduleData, mySchedule, user?.signedIn, updateMyScheduleSilent, createCalendarSignature])

  const value = useMemo<MyScheduleContextValue>(
    () => ({
      mySchedule,
      rescheduledSessions,
      loading: myScheduleApiState.loading || scheduleLoading,
      isReady: !!(user?.signedIn && mySchedule),
      addToMySchedule,
      removeFromMySchedule,
    }),
    [
      mySchedule,
      rescheduledSessions,
      myScheduleApiState.loading,
      scheduleLoading,
      user,
      addToMySchedule,
      removeFromMySchedule,
    ]
  )

  return <MyScheduleContext.Provider value={value}>{children}</MyScheduleContext.Provider>
}
